// Nadrama <https://nadrama.com>
// Copyright Nadrama Pty Ltd
// All rights reserved.
import * as z from "zod/mini";
import {
  zAccountCreateInputBody,
  zAccountSettingsInputBody,
  zClusterCreateInputBody,
  zClusterProjectInputBody,
  zGroupCreateInputBody,
  zGroupInputBody,
  zInvitationInputBody,
  zProfileInputBody,
  zProjectCreateInputBody,
  zProjectInputBody,
  zProviderInputBody,
} from "./api/generated/zod.gen";

const email = z.email();

export const accountCreateFormSchema = zAccountCreateInputBody.check(
  z.refine((value) => value.agreementAccepted, {
    error: "Accept the agreements to continue.",
    path: ["agreementAccepted"],
  }),
);
export const accountSettingsFormSchema = zAccountSettingsInputBody.check(
  z.refine(
    (value) =>
      value.notification_emails.every(
        (address) => z.safeParse(email, address).success,
      ),
    { error: "Enter a valid notification email address.", path: ["notification_emails"] },
  ),
);
export const groupCreateFormSchema = zGroupCreateInputBody;
export const groupUpdateFormSchema = zGroupInputBody;
export const invitationFormSchema = zInvitationInputBody;
export const profileFormSchema = zProfileInputBody;
export const projectCreateFormSchema = zProjectCreateInputBody;
export const projectUpdateFormSchema = zProjectInputBody;
export const clusterProjectFormSchema = zClusterProjectInputBody;
export const invitationAcceptanceFormSchema = z.object({
  consent: z.literal("accepted", { error: "Accept the agreements to continue." }),
});

export const providerFormSchema = zProviderInputBody.check(
  z.refine(
    (value) => value.kind !== "aws" || Boolean(value.awsAccountId),
    { error: "Enter a valid AWS account ID.", path: ["awsAccountId"] },
  ),
  z.refine(
    (value) =>
      value.kind !== "google" ||
      /^[a-z][a-z0-9-]{4,28}[a-z0-9]$/.test(value.googleProjectId ?? ""),
    { error: "Enter a valid Google Cloud project ID.", path: ["googleProjectId"] },
  ),
);

type IPv4Prefix = { address: number; bits: number };

function ipv4Prefix(value: string): IPv4Prefix | undefined {
  const [addressValue, bitsValue, extra] = value.split("/");
  const octets = addressValue?.split(".").map(Number) ?? [];
  const bits = Number(bitsValue);
  if (
    extra !== undefined ||
    octets.length !== 4 ||
    addressValue!.split(".").some((octet) => String(Number(octet)) !== octet) ||
    octets.some((octet) => !Number.isInteger(octet) || octet < 0 || octet > 255) ||
    !Number.isInteger(bits) ||
    bits < 0 ||
    bits > 32
  )
    return undefined;
  const address =
    (((octets[0]! << 24) |
      (octets[1]! << 16) |
      (octets[2]! << 8) |
      octets[3]!) >>>
      0);
  const mask = bits === 0 ? 0 : (0xffffffff << (32 - bits)) >>> 0;
  if ((address & mask) >>> 0 !== address) return undefined;
  return { address, bits };
}

function subnetWithin(networkValue: string, subnetValue: string): boolean {
  const network = ipv4Prefix(networkValue);
  const subnet = ipv4Prefix(subnetValue);
  if (!network || !subnet || network.bits !== 16 || subnet.bits !== 24)
    return false;
  const mask = (0xffffffff << (32 - network.bits)) >>> 0;
  return ((subnet.address & mask) >>> 0) === network.address;
}

export const clusterFormSchema = zClusterCreateInputBody.check(
  z.refine(
    (value) => {
      const network = ipv4Prefix(value.networkIpv4);
      return (
        network?.bits === 16 &&
        value.networkIpv4.startsWith("172.") &&
        Number(value.networkIpv4.split(".")[1]) >= 18 &&
        Number(value.networkIpv4.split(".")[1]) <= 30
      );
    },
    { error: "Select a valid recommended VPC/network.", path: ["networkIpv4"] },
  ),
  z.refine(
    (value) => subnetWithin(value.networkIpv4, value.publicSubnetCidrIpv4),
    { error: "Public subnet must be a /24 within the VPC/network.", path: ["publicSubnetCidrIpv4"] },
  ),
  z.refine(
    (value) => subnetWithin(value.networkIpv4, value.privateSubnetCidrIpv4),
    { error: "Private subnet must be a /24 within the VPC/network.", path: ["privateSubnetCidrIpv4"] },
  ),
  z.refine(
    (value) => value.publicSubnetCidrIpv4 !== value.privateSubnetCidrIpv4,
    { error: "Public and private subnets must not overlap.", path: ["publicSubnetCidrIpv4"] },
  ),
  z.refine(
    (value) => value.publicSubnetCidrIpv4 !== value.privateSubnetCidrIpv4,
    { error: "Public and private subnets must not overlap.", path: ["privateSubnetCidrIpv4"] },
  ),
  z.refine(
    (value) =>
      Boolean(value.region) &&
      value.zone.startsWith(value.region!) &&
      /^[a-z]$/.test(value.zone.slice(value.region!.length)),
    { error: "Select an availability zone in the cluster region.", path: ["zone"] },
  ),
);

export function formObject(data: FormData): Record<string, string> {
  return Object.fromEntries(data.entries()) as Record<string, string>;
}

export function accountCreateFormValue(data: FormData) {
  const value = formObject(data);
  return {
    userName: value.userName ?? "",
    organisation: value.organisation ?? "",
    country: value.country ?? "",
    timezone: value.timezone ?? "",
    agreementAccepted: value.agreementAccepted === "accepted",
  };
}

export function accountSettingsFormValue(data: FormData) {
  const value = formObject(data);
  return {
    name: value.name ?? "",
    organisation: value.organisation ?? "",
    country: value.country ?? "",
    notification_emails: [...data.entries()]
      .filter(([name]) => name.startsWith("notification_email_"))
      .map(([, address]) => String(address)),
  };
}

export function clusterFormValue(
  data: FormData,
  create: boolean,
): Record<string, string> & { mode: string; nodes: string } {
  const value = formObject(data);
  return {
    ...value,
    mode: value.mode ?? "direct",
    nodes: value.nodes ?? (create ? "1" : ""),
  };
}

export function providerFormValue(
  data: FormData,
  kind: "aws" | "google",
): Record<string, string> & { kind: "aws" | "google" } {
  return { ...formObject(data), kind };
}
